import { ServerError, ServerParseError } from "@apollo/client/errors";
import { ErrorCode } from "@shuttlerock/api-types/federated";
import { getAccessTokenSilently, isTokenExpiringWithin } from "@shuttlerock/auth";
import type { ReconnectDecision, SubscriptionTerminal } from "./reconnectingSubscriptionLink";

/**
 * The subgraph tears a subscription down — and refuses a new one — once the
 * viewer's token is within five minutes of expiring. auth0-spa-js serves its
 * cached token until 60s before expiry, so inside that window every attempt
 * re-sends a token the subgraph has already decided to reject; only a forced
 * refresh clears it. The extra minute keeps us ahead of the server's threshold.
 */
const STALENESS_WINDOW_MS = 6 * 60 * 1_000;

/**
 * Error codes a retry can never clear: the viewer's grant, the document, or the
 * arguments are wrong, and every attempt reproduces them exactly. Anything else
 * stays retryable — including the `UNKNOWN` that unrecognised subgraph errors
 * map to, which is what the staleness rejection itself arrives as — so the
 * link's attempt cap remains the backstop for whatever is not classified here.
 */
const PERMANENT_ERROR_CODES: ReadonlySet<string> = new Set([
  ErrorCode.Unauthorised,
  ErrorCode.NotAuthorised,
  ErrorCode.NotFound,
  ErrorCode.Validation,
  ErrorCode.InvalidArgument,
]);

/**
 * Statuses that describe the request rather than the moment. 401 is pointedly
 * absent: a rejected token is the one failure a refresh does fix.
 */
const PERMANENT_HTTP_STATUSES: ReadonlySet<number> = new Set([400, 403, 404, 422]);

const STOP: ReconnectDecision = { retry: false };
const RETRY_WITH_FRESH_CREDENTIALS: ReconnectDecision = { retry: true, refreshCredentials: true };
const RETRY: ReconnectDecision = { retry: true, refreshCredentials: false };

const responseStatus = (error: unknown): number | undefined =>
  ServerError.is(error) || ServerParseError.is(error) ? error.response.status : undefined;

/** Decides what a subscription's terminal is worth retrying, and on what credentials. */
export const classifySubscriptionTerminal = (terminal: SubscriptionTerminal): ReconnectDecision => {
  if (terminal.kind === "errorFrame") {
    const isPermanent = terminal.errors.some((error) => {
      const code = error.extensions?.code;
      return typeof code === "string" && PERMANENT_ERROR_CODES.has(code);
    });

    return isPermanent ? STOP : RETRY_WITH_FRESH_CREDENTIALS;
  }

  if (terminal.kind === "error") {
    const status = responseStatus(terminal.error);
    if (status !== undefined && PERMANENT_HTTP_STATUSES.has(status)) {
      return STOP;
    }

    return status === 401 ? RETRY_WITH_FRESH_CREDENTIALS : RETRY;
  }

  // A clean complete is the staleness recycle, so the reconnect needs a token
  // the subgraph will accept — which the cached one, by then, is not.
  return RETRY_WITH_FRESH_CREDENTIALS;
};

let inFlightRefresh: Promise<void> | undefined;

const forceRefreshIfStale = async (): Promise<void> => {
  // The cached read is local in the common case, and tells us whether a forced
  // grant would change anything. Outside the staleness window it would not: the
  // token we already hold is one the subgraph accepts.
  const { accessToken } = await getAccessTokenSilently();
  if (accessToken && !isTokenExpiringWithin(accessToken, STALENESS_WINDOW_MS)) {
    return;
  }

  await getAccessTokenSilently({ cacheMode: "off" });
};

/**
 * Refreshes the access token, but only when it is close enough to expiry for the
 * subgraph to start rejecting it.
 *
 * Deduplicated across callers because every mounted subscription hits the
 * staleness boundary at the same instant. auth0-spa-js collapses the concurrent
 * token grants on its own, but the auth client re-fetches the viewer and
 * notifies its subscribers on each new token, so N un-deduplicated callers cost
 * N `me` queries and N app-wide re-renders.
 */
export const refreshSubscriptionCredentials = (): Promise<void> => {
  inFlightRefresh ??= forceRefreshIfStale().finally(() => {
    inFlightRefresh = undefined;
  });

  return inFlightRefresh;
};
